import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Container, Row } from 'react-bootstrap'

import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'

import { resetTasks, selectTasks, selectTasksLoading } from '../../../store/task/task'
import { GetTasksFilters } from '../../../store/meetingSupport/meetingSupport'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { useGetUsersQuery } from '../../../store/user/user'

import { UserTagDropdownItemOption } from '../../../components/SelectDropdown/UserTagDropdownItemOption'
import { SelectDropdown, SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { Divider } from '../../../components/Divider/Divider'
import { Button } from '../../../components/Button/Button'

import { ExternalUserServiceIntegration, User } from '../../../models/User'

import { AddTaskToCRMModal } from '../../MeetingSummary/CRM/V2/AddTaskToCRMModal'
import { UserTaskRow, UserTasksHeader } from '../../Tasks/Tasks'
import { TaskSourceType, useTaskSources, useUserTasks } from '../../Tasks/hooks'
import { Task } from '../../../models/MeetingSupport'

interface ContactTasksProps {
  contact: User
}

export const ContactTasks: React.FC<ContactTasksProps> = ({ contact }) => {
  const dispatch = useAppDispatch()
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)

  const tasks = useAppSelector(selectTasks)
  const isLoading = useAppSelector(selectTasksLoading)

  const [selectedUserIntegration, setSelectedUserIntegration] = useState<ExternalUserServiceIntegration | null>(null)
  const [showCRMModal, setShowCRMModal] = useState<boolean>(false)
  const [filters, setFilters] = useState<GetTasksFilters>({})

  const { taskSources } = useTaskSources()

  const getFetchTasksFilters = useCallback(
    (filters: GetTasksFilters) => {
      const { id: contactId } = contact

      const baseFilters = { type: filters.type, status: filters.status }

      const assignedToContactFilters = { ...baseFilters, assignedTo: contactId }
      const relatedToContactFilters = { ...baseFilters, relatedTo: contactId }

      if (!filters.assignedTo) {
        return [assignedToContactFilters, relatedToContactFilters]
      }

      if (filters.assignedTo === contactId) {
        return [assignedToContactFilters]
      }

      const assignedToUserAndRelatedToContactFilters = {
        ...baseFilters,
        assignedTo: filters.assignedTo,
        relatedTo: contactId,
      }
      return [assignedToUserAndRelatedToContactFilters]
    },
    [contact],
  )

  const { isLoadingMore, handleAddMoreTasks, reloadTasks } = useUserTasks({ filters, getFetchTasksFilters })

  useEffect(() => {
    return () => {
      dispatch(resetTasks())
    }
  }, [dispatch])

  const sortedTasks = useMemo(() => {
    const getTaskRank = ({ assignedTo }: Task) => {
      if (assignedTo?.id === currentUser.id) {
        return 0
      }
      if (assignedTo?.id === contact.id) {
        return 1
      }
      return 2
    }
    return [...tasks].sort((a, b) => getTaskRank(a) - getTaskRank(b))
  }, [tasks, currentUser.id, contact.id])

  const handleShowCRMModal = (userIntegration: ExternalUserServiceIntegration) => {
    setSelectedUserIntegration(userIntegration)
    setShowCRMModal(true)
  }

  const handleHideCRMModal = (isSaved?: boolean) => {
    if (isSaved) {
      reloadTasks()
    }
    setSelectedUserIntegration(null)
    setShowCRMModal(false)
  }

  const handleResetFilters = () => {
    setFilters({})
  }

  const Loader = () => <div className='skeleton mt-2' style={{ width: '100%', height: '300px' }} />

  return (
    <>
      {showCRMModal && selectedUserIntegration ? (
        <AddTaskToCRMModal
          show={showCRMModal}
          onHide={handleHideCRMModal}
          userIntegration={selectedUserIntegration}
          tasks={tasks}
          taskContextDetails={{ contactId: contact.id }}
          type={TaskSourceType.CONTACT}
        />
      ) : null}
      <Container className='d-flex flex-column gap-3 p-0' fluid>
        <ContactTaskFilters contact={contact} filters={filters} setFilters={setFilters} onResetFilters={handleResetFilters} />
        <UserTasksHeader onShowCRMModal={handleShowCRMModal} />
        <Divider />
        {isLoading ? (
          <Loader />
        ) : (
          <>
            {!tasks.length ? (
              <Row className='zocks-p-2.0'>
                <span className='paragraph-medium'>No tasks found.</span>
              </Row>
            ) : (
              sortedTasks.map((task, index) => {
                return (
                  <React.Fragment key={task.id}>
                    <UserTaskRow task={task} taskSources={taskSources} />
                    {index !== tasks.length - 1 ? <Divider /> : null}
                  </React.Fragment>
                )
              })
            )}
            <div className='w-100 d-flex align-items-center justify-content-center mt-3'>
              <Button variant='darkGhost' className='button-medium' onClick={handleAddMoreTasks} loading={isLoadingMore}>
                Load more tasks...
              </Button>
            </div>
          </>
        )}
      </Container>
    </>
  )
}

const zocksUserToSelectOption = (user: User) => {
  return {
    id: user.id,
    label: getUserDisplayName(user),
  }
}

interface ContactTaskFiltersProps {
  contact: User
  filters: GetTasksFilters
  setFilters: React.Dispatch<React.SetStateAction<GetTasksFilters>>
  onResetFilters: () => void
}

const ContactTaskFilters: React.FC<ContactTaskFiltersProps> = ({ contact, filters, setFilters, onResetFilters }) => {
  const accountId = useAppSelector(selectAccountId)

  const [searchInput, setSearchInput] = useState<string>('')

  const { data: assignedToUsers, isFetching } = useGetUsersQuery({
    accountId,
    name: searchInput,
    identityTypes: ['idp'],
    pageRequest: { size: 50, page: 0 },
  })

  const contactAssignedToOption = useMemo(() => zocksUserToSelectOption(contact), [contact])

  const assignedToOptions = useMemo<SelectOption[]>(() => {
    if (!assignedToUsers) {
      return []
    }
    const userAssignedToOptions = assignedToUsers.data.map(zocksUserToSelectOption)
    return [contactAssignedToOption, ...userAssignedToOptions]
  }, [contactAssignedToOption, assignedToUsers])

  const sourceOptions = useMemo<SelectOption[]>(
    () => [
      { id: 'email', label: 'E-mail' },
      { id: 'meeting', label: 'Meeting' },
    ],
    [],
  )

  const statusOptions = useMemo<SelectOption[]>(
    () => [
      { id: 'closed', label: 'Closed' },
      { id: 'open', label: 'Open' },
    ],
    [],
  )

  const selectedAssignedToOption = useMemo<SelectOption | null>(() => {
    const { assignedTo } = filters
    if (!assignedTo) {
      return null
    }
    const assignedToUser = assignedToUsers?.data.find((e) => e.id === assignedTo)
    if (!assignedToUser) {
      return null
    }
    return zocksUserToSelectOption(assignedToUser)
  }, [filters, assignedToUsers])

  const selectedSourceOption = useMemo<SelectOption | null>(() => {
    const { type } = filters
    if (!type) {
      return null
    }
    const sourceOption = sourceOptions.find((e) => e.id === type)
    if (!sourceOption) {
      return null
    }
    return sourceOption
  }, [filters, sourceOptions])

  const selectedStatusOption = useMemo<SelectOption | null>(() => {
    const { status } = filters
    if (!status) {
      return null
    }
    const statusOption = statusOptions.find((e) => e.id === status)
    if (!statusOption) {
      return null
    }
    return statusOption
  }, [filters, statusOptions])

  const handleAssignedToOptionSelected = (option: SelectOption) => {
    setFilters((prev) => ({ ...prev, assignedTo: option.id }))
  }

  const handleAssignedToOptionDeselected = () => {
    setFilters((prev) => ({ ...prev, assignedTo: undefined }))
  }

  const handleSourceOptionSelected = (option: SelectOption) => {
    setFilters((prev) => ({ ...prev, type: option.id }))
  }

  const handleSourceOptionDeselected = () => {
    setFilters((prev) => ({ ...prev, type: undefined }))
  }

  const handleStatusOptionSelected = (option: SelectOption) => {
    setFilters((prev) => ({ ...prev, status: option.id }))
  }

  const handleStatusOptionDeselected = () => {
    setFilters((prev) => ({ ...prev, status: undefined }))
  }

  const handleAssignedToSearchInputChange = async (searchInput: string) => {
    setSearchInput(searchInput)
  }

  return (
    <div className='d-flex align-items-center gap-2'>
      <div className='d-flex flex-column gap-1 w-100'>
        <span className='lead-extra-small'>Filter by assignee</span>
        <SelectDropdown
          placeholder='Choose assignee'
          searchable={true}
          onSearchInputChange={handleAssignedToSearchInputChange}
          deselectOptionLabel='None'
          options={assignedToOptions}
          initialSelectedOption={selectedAssignedToOption}
          showDeselectOption={true}
          onOptionSelected={handleAssignedToOptionSelected}
          onDeselect={handleAssignedToOptionDeselected}
          loading={isFetching}
          DropDownItemOptionComponent={UserTagDropdownItemOption}
        />
      </div>
      <div className='d-flex flex-column gap-1 w-100'>
        <span className='lead-extra-small'>Filter by source</span>
        <SelectDropdown
          placeholder='Choose source'
          deselectOptionLabel='None'
          options={sourceOptions}
          initialSelectedOption={selectedSourceOption}
          showDeselectOption={true}
          onOptionSelected={handleSourceOptionSelected}
          onDeselect={handleSourceOptionDeselected}
        />
      </div>
      <div className='d-flex flex-column gap-1 w-100'>
        <span className='lead-extra-small'>Filter by status</span>
        <SelectDropdown
          placeholder='Choose status'
          deselectOptionLabel='None'
          options={statusOptions}
          initialSelectedOption={selectedStatusOption}
          showDeselectOption={true}
          onOptionSelected={handleStatusOptionSelected}
          onDeselect={handleStatusOptionDeselected}
        />
      </div>
      <Button onClick={onResetFilters}>
        <i className='bi bi-arrow-clockwise' />
        <span className='paragraph-small bold' style={{ width: 'max-content' }}>
          Clear all
        </span>
      </Button>
    </div>
  )
}
